import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, PenLine, LogIn, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useT } from '../context/LangContext';
import { LangSwitcher } from './LangSwitcher';
import PartnerLockup from './PartnerLockup';

export default function Header() {
  var navigate = useNavigate();
  var location = useLocation();
  var auth = useAuth();
  var t = useT();
  var [mobileOpen, setMobileOpen] = useState(false);

  function go(path) { navigate(path); setMobileOpen(false); }
  function isActive(path) { return location.pathname === path; }

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-lg border-b border-gray-100" data-testid="main-header">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-[72px]">

          {/* LEFT — YEFFA programme identity (primary) */}
          <button onClick={function() { go('/'); }} className="flex items-center gap-3 group max-w-[60%] md:max-w-none" data-testid="nav-logo">
            <div className="leading-tight">
              <span
                className="font-serif text-[15px] md:text-[17px] text-[#07743F] block leading-[1.15] tracking-tight font-semibold"
                data-testid="nav-programme-name"
              >
                {t('brand.yeffa_full', 'Youth Entrepreneurship for the Future of Food and Agriculture')}
              </span>
              <span className="kicker text-[#58595B]">YEFFA</span>
            </div>
          </button>

          {/* CENTRE — Navigation (max 2 items) */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            <button
              onClick={function() { go('/stories'); }}
              className={'text-sm font-medium transition-colors relative pb-1 ' + (isActive('/stories') ? 'text-[#07743F]' : 'text-gray-500 hover:text-gray-900')}
              data-testid="nav-stories"
            >
              {t('nav.stories', 'Stories')}
              {isActive('/stories') && <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#07743F] rounded-full" />}
            </button>
            <button
              onClick={function() { go('/how'); }}
              className={'text-sm font-medium transition-colors relative pb-1 ' + (isActive('/how') ? 'text-[#07743F]' : 'text-gray-500 hover:text-gray-900')}
              data-testid="nav-how"
            >
              {t('nav.journey', 'The Journey')}
              {isActive('/how') && <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#07743F] rounded-full" />}
            </button>
          </nav>

          {/* RIGHT — Actions */}
          <div className="hidden md:flex items-center gap-5">
            <LangSwitcher />
            {auth.isAuthenticated ? (
              <div className="relative group">
                <button className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors">
                  <div className="w-8 h-8 bg-[#07743F] rounded-full flex items-center justify-center text-white text-xs font-bold">
                    {auth.user ? auth.user.name.charAt(0) : 'U'}
                  </div>
                  <ChevronDown className="w-3 h-3" />
                </button>
                <div className="absolute top-full right-0 mt-2 w-44 bg-white rounded-xl shadow-lg border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="py-1.5">
                    {auth.isAdmin ? (
                      <button onClick={function() { go('/admin'); }} className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 rounded-lg" data-testid="nav-admin-dashboard">
                        {t('nav.dashboard', 'Dashboard')}
                      </button>
                    ) : (
                      <button onClick={function() { go('/contributor'); }} className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 rounded-lg" data-testid="nav-my-dashboard">
                        {t('nav.my_stories', 'My Stories')}
                      </button>
                    )}
                    <button onClick={function() { auth.logout(); go('/'); }} className="w-full px-4 py-2.5 text-left text-sm text-red-500 hover:bg-red-50 rounded-lg" data-testid="nav-logout">
                      {t('nav.sign_out', 'Sign Out')}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <button onClick={function() { go('/login'); }} className="text-sm text-gray-400 hover:text-gray-700 transition-colors" data-testid="nav-login">
                {t('nav.sign_in', 'Sign In')}
              </button>
            )}

            <Button
              onClick={function() { go('/submit'); }}
              className="cta-alive bg-[#07743F] hover:bg-[#065a32] text-white rounded-full px-6 py-2.5 text-sm font-medium shadow-sm"
              data-testid="nav-submit"
            >
              <PenLine className="w-4 h-4 mr-2" />
              {t('cta.tell_your_story', 'Tell Your Story')}
            </Button>

            {/* FAR RIGHT — Partner lockup. Separated from CTA with breathing space + divider. */}
            <span className="hidden xl:block w-px h-9 bg-gray-200 ml-2" aria-hidden="true" />
            <div className="hidden xl:flex pl-1" data-testid="header-partner-lockup">
              <PartnerLockup variant="default" size="sm" />
            </div>
          </div>

          {/* MOBILE — Toggle */}
          <button onClick={function() { setMobileOpen(!mobileOpen); }} className="md:hidden p-2 text-gray-500 hover:text-gray-900 rounded-lg" data-testid="mobile-menu-toggle">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* MOBILE — Menu */}
        {mobileOpen && (
          <div className="md:hidden border-t border-gray-100 py-6 space-y-1">
            <div className="px-4 pb-3">
              <LangSwitcher />
            </div>
            <button onClick={function() { go('/stories'); }} className={'w-full px-4 py-3 text-left text-sm font-medium rounded-lg transition-colors ' + (isActive('/stories') ? 'bg-green-50 text-[#07743F]' : 'text-gray-700 hover:bg-gray-50')} data-testid="mobile-nav-stories">
              {t('nav.stories', 'Stories')}
            </button>
            <button onClick={function() { go('/how'); }} className={'w-full px-4 py-3 text-left text-sm font-medium rounded-lg transition-colors ' + (isActive('/how') ? 'bg-green-50 text-[#07743F]' : 'text-gray-700 hover:bg-gray-50')} data-testid="mobile-nav-how">
              {t('nav.journey', 'The Journey')}
            </button>

            <div className="pt-4 mt-4 border-t border-gray-100 space-y-1">
              <button onClick={function() { go('/submit'); }} className="w-full px-4 py-3 text-left text-sm font-semibold text-[#07743F] bg-green-50 rounded-lg flex items-center gap-2" data-testid="mobile-nav-submit">
                <PenLine className="w-4 h-4" />
                {t('cta.tell_your_story', 'Tell Your Story')}
              </button>

              {auth.isAuthenticated ? (
                <>
                  <button onClick={function() { go(auth.isAdmin ? '/admin' : '/contributor'); }} className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 rounded-lg" data-testid="mobile-nav-dashboard">
                    {auth.isAdmin ? t('nav.dashboard', 'Dashboard') : t('nav.my_stories', 'My Stories')}
                  </button>
                  <button onClick={function() { auth.logout(); go('/'); }} className="w-full px-4 py-3 text-left text-sm text-red-500 hover:bg-red-50 rounded-lg" data-testid="mobile-nav-logout">
                    {t('nav.sign_out', 'Sign Out')}
                  </button>
                </>
              ) : (
                <button onClick={function() { go('/login'); }} className="w-full px-4 py-3 text-left text-sm text-gray-400 hover:bg-gray-50 rounded-lg" data-testid="mobile-nav-login">
                  {t('nav.sign_in', 'Sign In')}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
